import React from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Paragraph,
  Button,
  PrintIcon,
  DownloadIcon,
  ChatMinimizeIcon,
  Headings,
  Link,
} from '@sentara-health/optimahealth_web';
import { useNavigate } from 'react-router-dom';
import { formatMedicalText, toSentancePascalCase, DeviceTypes, useDeviceType, getGreeting, getNetworkLabel, domainForSegment } from 'common';
import { ClaimResponse } from '@sentara-health/sentara-api-hooks-core';
import { memberBaseUrl } from '../../../common/utils/ssoEndPoints/Brands/Sentara';
import { useMedicalClaimItem } from '../../../common/utils/ClaimUtils';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';

export interface SuperEOBHeaderProps {
  claimData: ClaimResponse;
  claimId: string;
  memberName: string;
  memberId?: string;
  showMedical?: boolean;
  onDownloadEOB?: (claimId: string) => void;
  isDownloading?: boolean;
  phoneNumber?: string;
}

const SuperEOBHeader: React.FC<SuperEOBHeaderProps> = ({
  claimData,
  claimId,
  memberName,
  memberId,
  showMedical,
  onDownloadEOB,
  isDownloading = false,
  phoneNumber
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;
  const { network, providerName, serviceDate, typeOfCare } = useMedicalClaimItem(claimData, 0);
  const { contentListingItemClicked } = useAdobeAnalytics();
  const isMedicalClaim = showMedical !== false;

  const trackDownloadEobClick = () => {
    contentListingItemClicked({
      segment: `${domainForSegment}${isMedicalClaim ? '/home/medical-claims' : '/home/behavioral-claims'}`,
      contentListing: {
        eventType: isMedicalClaim ? 'Medical_Download_EOB_claim_click' : 'Behavioral_Download_EOB_claim_click',
        listing: {
          content: [
            {
              contentId: isMedicalClaim ? '1221' : '1228',
              contentTitle: isMedicalClaim ? 'Medical_Download_EOB_claim_click' : 'Behavioral_Download_EOB_claim_click',
              itemPosition: isMedicalClaim ? 13 : 25,
            },
          ],
        },
      },
    });
  };

  const greeting = getGreeting(t);
  const handlePrint = (e: React.MouseEvent) => {
    e.preventDefault();
    globalThis.print();
  };

  const handleDownloadPDF = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isDownloading) return;
    if (onDownloadEOB && claimId) {
      trackDownloadEobClick();
      onDownloadEOB(claimId);
    }
  };

  const showVisitDescription = Boolean(claimData?.created) && providerName !== '—';

  return (
    <div className={"mb-5"}>
      {/* Header Buttons */}
      <div className="d-flex justify-content-between align-items-center mb-4 not-print">
        <Button
          iconLeft={<ChatMinimizeIcon />}
          data-testid="Backbtn"
          variant="icon_btn"
          onClick={() => navigate(-1)}
        >
          {t('common.back')}
        </Button>

        {isMobile ? null : (
          <div className="d-flex gap-3">
            <Button
              iconLeft={<PrintIcon color="black" />}
              data-testid="Printbtn"
              variant="icon_btn"
              onClick={handlePrint}
            >
              {t('common.print')}
            </Button>

            <Button
              iconLeft={<DownloadIcon color="black" />}
              data-testid="Downloadbtn"
              variant="icon_btn"
              onClick={handleDownloadPDF}
              disabled={isDownloading || !onDownloadEOB}
            >
              {t('common.download')}
            </Button>
          </div>
        )}
      </div>

      {/* Title */}
      <Headings
        level={2}
        variant="bold"
        text={t('medicalClaims.explanationOfBenefits')}
        className="m-0 p-0"
      />

      <div className="claim-details mt-4">
        <div className={isMobile ? "rounded-4 p-4 border virtual-border virtual-border-right mb-4" : "row mb-2"}>
          {/* Left Column */}
          <div className="col-12 col-md-6 mb-3">
            <Paragraph
              className="m-0"
              text={
                <Trans
                  i18nKey="medicalClaims.patientNameDisplay"
                  values={{ name: toSentancePascalCase(memberName) }}
                  components={{ strong: <strong /> }}
                />
              }
            />
            <Paragraph
              className="m-0 mt-2"
              text={
                <Trans
                  i18nKey="claimsSummary.memberIdDisplay"
                  values={{ memberId: claimData?.memberId ?? memberId ?? "—" }}
                  components={{ strong: <strong /> }}
                />
              }
            />
            <Paragraph
              className="m-0 mt-2"
              text={
                <Trans
                  i18nKey="claimsSummary.claimIdDisplay"
                  values={{ claimId: claimId ?? "—" }}
                  components={{ strong: <strong /> }}
                />
              }
            />
          </div>

          {isMobile ? null : (
            <div className="col-12 col-md-6 d-flex justify-content-md-end mt-3 mt-md-0">
              <div className="d-flex flex-column text-start">
                <Paragraph
                  variant="bold"
                  className="m-0"
                  text={t('medicalClaims.healthPlans')}
                />
                <Link
                  to={`tel:${phoneNumber}`}
                  className="text-decoration-underline mt-2"
                >
                  {phoneNumber}
                </Link>
                <Link
                  className="maintenance-link mt-2"
                  to={memberBaseUrl}
                  target="_blank"
                  data-testid="websiteLink"
                >
                  {t("medicalClaims.websiteDisplay")}
                </Link>
              </div>
            </div>
          )}
        </div>

        {isMobile ? null : <hr className="my-4" />}
      </div>

      {isMobile ? (
        <div className="w-100 px-0">
          {/* Greeting Card */}
          <div className={isMobile ? "rounded-4 p-4 border virtual-border virtual-border-right mb-4" : "row mb-2"}>
              <Headings
                level={3}
                className="m-0"
                variant="medium"
                text={
                  <Trans
                    i18nKey="medicalClaims.namedGreeting"
                    values={{ greeting: greeting, name: toSentancePascalCase(memberName) }}
                    components={{ strong: <strong /> }}
                  />
                }
              />
              <Headings
                level={3}
                className="m-0"
                variant="medium"
                text={
                  <Trans
                    i18nKey="medicalClaims.greetingDescription"
                  />
                }
              />
          </div>
        </div>
      ) : null}

      {/* DESKTOP VISIT DESCRIPTION */}
      {showVisitDescription ? (
        <div className={isMobile ? "rounded-4 p-4 border virtual-border virtual-border-right mb-4" : "row mb-2"}>
          <Headings
            level={3}
            variant="medium"
            className="mb-4"
            text={
              <Trans
                i18nKey="medicalClaims.visitDescriptionSentence"
                components={{
                  green: <span className="text-shaded-green" />
                }}
                values={{
                  typeOfCare: formatMedicalText(typeOfCare),
                  serviceDate,
                  network: getNetworkLabel(network, t('medicalClaims.inNetwork'), t('medicalClaims.outOfNetwork')),
                  providerName: formatMedicalText(providerName),
                }}
              />
            }
          />
        </div>
      ) : null}
    </div>
  );
};

export default SuperEOBHeader;